import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, Link2, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useMunicipality } from "@/context/MunicipalityContext";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";
import {
  createMunicipalityResourceLink,
  deleteMunicipalityResourceLink,
  generateMunicipalityResourceLinkDescription,
  listMunicipalityResourceLinks,
  type MunicipalityResourceLink,
  type MunicipalityResourceLinkInput,
  updateMunicipalityResourceLink,
} from "@/api/municipality_resource_links";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/useToast";

const linksQueryKey = (municipalityId: number) => ["municipalityResourceLinks", municipalityId] as const;

function LabelWithInfoTooltip({
  htmlFor,
  label,
  hintId,
  hintText,
}: {
  htmlFor: string;
  label: string;
  hintId: string;
  hintText: string;
}) {
  return (
    <>
      <div className="flex items-center gap-1.5">
        <Label htmlFor={htmlFor}>{label}</Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex shrink-0 rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={`More about ${label}`}
            >
              <Info className="h-3.5 w-3.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent
              side="bottom"
              sideOffset={6}
              collisionPadding={16}
              className="z-[100] max-w-xs text-left font-normal"
            >
              {hintText}
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </div>
      <span id={hintId} className="sr-only">
        {hintText}
      </span>
    </>
  );
}

function parseKeywords(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((k) => k.trim())
    .filter((k) => k.length > 0);
}

function emptyForm(): MunicipalityResourceLinkInput {
  return {
    title: "",
    url: "",
    cta_label: "",
    description: null,
    resource_type: null,
    keywords: [],
    priority: 0,
    active: true,
  };
}

function linkToForm(link: MunicipalityResourceLink): MunicipalityResourceLinkInput {
  return {
    title: link.title,
    url: link.url,
    cta_label: link.cta_label,
    description: link.description ?? null,
    resource_type: link.resource_type ?? null,
    keywords: Array.isArray(link.keywords) ? [...link.keywords] : [],
    priority: link.priority,
    active: link.active,
  };
}

export function MunicipalityResourceLinksManager() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { municipalities, isLoading: municipalitiesLoading } = useMunicipality();

  const eligibleMunicipalities = useMemo(
    () => municipalities.filter((m) => m.municipality_resource_links_access === true),
    [municipalities]
  );

  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<MunicipalityResourceLink | null>(null);
  const [form, setForm] = useState<MunicipalityResourceLinkInput>(emptyForm);
  const [keywordsText, setKeywordsText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<MunicipalityResourceLink | null>(null);

  useEffect(() => {
    if (eligibleMunicipalities.length === 1 && selectedMunicipalityId === null) {
      setSelectedMunicipalityId(eligibleMunicipalities[0].id);
    }
  }, [eligibleMunicipalities, selectedMunicipalityId]);

  const {
    data: links = [],
    isLoading: linksLoading,
    error: linksError,
  } = useQuery({
    queryKey: selectedMunicipalityId ? linksQueryKey(selectedMunicipalityId) : ["municipalityResourceLinks", "none"],
    queryFn: () => listMunicipalityResourceLinks(selectedMunicipalityId!),
    enabled: selectedMunicipalityId !== null,
  });

  const invalidateLinks = useCallback(() => {
    if (selectedMunicipalityId) {
      void queryClient.invalidateQueries({ queryKey: linksQueryKey(selectedMunicipalityId) });
    }
  }, [queryClient, selectedMunicipalityId]);

  const createMutation = useMutation({
    mutationFn: (payload: MunicipalityResourceLinkInput) =>
      createMunicipalityResourceLink(selectedMunicipalityId!, payload),
    onSuccess: () => {
      invalidateLinks();
      setDialogOpen(false);
      setEditingLink(null);
      setForm(emptyForm());
      setKeywordsText("");
      toast({ title: "Link created" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not create link", description: e.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: MunicipalityResourceLinkInput }) =>
      updateMunicipalityResourceLink(selectedMunicipalityId!, id, payload),
    onSuccess: () => {
      invalidateLinks();
      setDialogOpen(false);
      setEditingLink(null);
      setForm(emptyForm());
      setKeywordsText("");
      toast({ title: "Link updated" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not update link", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteMunicipalityResourceLink(selectedMunicipalityId!, id),
    onSuccess: () => {
      invalidateLinks();
      setDeleteTarget(null);
      toast({ title: "Link deleted" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not delete link", description: e.message, variant: "destructive" });
    },
  });

  // ENG-1390: LLM-draft a scenario-rich description from the form's current fields.
  // Stateless server call; the admin reviews/edits the draft before saving.
  const generateDescriptionMutation = useMutation({
    mutationFn: () =>
      generateMunicipalityResourceLinkDescription(selectedMunicipalityId!, {
        title: form.title.trim(),
        resource_type: (form.resource_type ?? "").trim() || null,
        url: form.url.trim() || null,
        keywords: parseKeywords(keywordsText),
      }),
    onSuccess: ({ description }) => {
      setForm((f) => ({ ...f, description }));
    },
    onError: (e: Error) => {
      toast({ title: "Could not generate description", description: e.message, variant: "destructive" });
    },
  });

  const openCreate = () => {
    setEditingLink(null);
    setForm(emptyForm());
    setKeywordsText("");
    setDialogOpen(true);
  };

  const openEdit = (link: MunicipalityResourceLink) => {
    setEditingLink(link);
    const next = linkToForm(link);
    setForm(next);
    setKeywordsText(next.keywords.join(", "));
    setDialogOpen(true);
  };

  const submitForm = () => {
    const keywords = parseKeywords(keywordsText);
    const descTrimmed = (form.description ?? "").trim();
    const typeTrimmed = (form.resource_type ?? "").trim();
    const payload: MunicipalityResourceLinkInput = {
      ...form,
      description: descTrimmed === "" ? null : descTrimmed,
      resource_type: typeTrimmed === "" ? null : typeTrimmed,
      keywords,
    };

    if (editingLink) {
      updateMutation.mutate({ id: editingLink.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  if (municipalitiesLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (eligibleMunicipalities.length === 0) {
    return (
      <div className="h-full overflow-y-auto bg-slate-50 p-6">
        <Card className="mx-auto max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Resource links
            </CardTitle>
            <CardDescription>
              No municipalities are enabled for resource link management on your account. Ask your Ordinal admin to turn
              on &quot;Municipality Resource Links Manager&quot; for the relevant team.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="mx-auto w-full max-w-6xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Municipality resource links</h1>
          <p className="mt-1 text-sm text-slate-600">
            Manage recommended links used by the assistant for your municipality. Lower priority numbers are preferred
            when several links match.
          </p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Municipality</CardTitle>
            <CardDescription>Select which municipality you are editing.</CardDescription>
          </CardHeader>
          <CardContent className="max-w-md">
            <Label htmlFor="mrl-municipality" className="sr-only">
              Municipality
            </Label>
            <Select
              value={selectedMunicipalityId !== null ? String(selectedMunicipalityId) : ""}
              onValueChange={(v) => setSelectedMunicipalityId(Number(v))}
              disabled={eligibleMunicipalities.length === 1}
            >
              <SelectTrigger id="mrl-municipality">
                <SelectValue placeholder="Choose a municipality" />
              </SelectTrigger>
              <SelectContent>
                {eligibleMunicipalities.map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {formatMunicipalityLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {selectedMunicipalityId === null && eligibleMunicipalities.length > 1 && (
          <Alert>
            <AlertDescription>Choose a municipality to view and edit resource links.</AlertDescription>
          </Alert>
        )}

        {selectedMunicipalityId !== null && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base">Links</CardTitle>
                <CardDescription>Active links are eligible for matching; inactive links are hidden.</CardDescription>
              </div>
              <Button size="sm" onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add link
              </Button>
            </CardHeader>
            <CardContent>
              {linksError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>
                    {(linksError as Error).message || "Failed to load links."}
                  </AlertDescription>
                </Alert>
              )}
              {linksLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ScrollArea className="h-[min(60vh,520px)] rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[72px]">Active</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>URL</TableHead>
                        <TableHead className="w-[100px]">Priority</TableHead>
                        <TableHead className="w-[120px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {links.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No links yet. Add one to get started.
                          </TableCell>
                        </TableRow>
                      ) : (
                        links.map((link) => (
                          <TableRow key={link.id}>
                            <TableCell>
                              <Badge variant={link.active ? "default" : "secondary"}>
                                {link.active ? "On" : "Off"}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{link.title}</TableCell>
                            <TableCell className="max-w-[240px] truncate text-muted-foreground">{link.url}</TableCell>
                            <TableCell>{link.priority}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" onClick={() => openEdit(link)} aria-label="Edit link">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteTarget(link)}
                                aria-label="Delete link"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <TooltipProvider delayDuration={200}>
              <DialogHeader>
                <DialogTitle>{editingLink ? "Edit resource link" : "New resource link"}</DialogTitle>
                <DialogDescription>
                  Recommended links can appear in chat when a resident&apos;s question matches. Each label has an info
                  button—hover or keyboard-focus it for field-specific guidance.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-2">
                <div className="space-y-2">
                  <LabelWithInfoTooltip
                    htmlFor="mrl-title"
                    label="Title"
                    hintId="mrl-title-hint"
                    hintText='Name shown with the suggestion so people know what the link is for (e.g. "Building permit application").'
                  />
                  <Input
                    id="mrl-title"
                    aria-describedby="mrl-title-hint"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="Building permit application"
                  />
                </div>
                <div className="space-y-2">
                  <LabelWithInfoTooltip
                    htmlFor="mrl-url"
                    label="URL"
                    hintId="mrl-url-hint"
                    hintText="Full web address (https). Opens when someone clicks the button in chat."
                  />
                  <Input
                    id="mrl-url"
                    type="url"
                    aria-describedby="mrl-url-hint"
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder="https://example.gov/permits"
                  />
                </div>
                <div className="space-y-2">
                  <LabelWithInfoTooltip
                    htmlFor="mrl-cta"
                    label="Button label (CTA)"
                    hintId="mrl-cta-hint"
                    hintText='Short label on the chat button. Keep it action-oriented (e.g. "Apply for a permit").'
                  />
                  <Input
                    id="mrl-cta"
                    aria-describedby="mrl-cta-hint"
                    value={form.cta_label}
                    onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))}
                    placeholder="Apply for a permit"
                  />
                </div>
                <div className="space-y-2">
                  <LabelWithInfoTooltip
                    htmlFor="mrl-desc"
                    label="Description (optional)"
                    hintId="mrl-desc-hint"
                    hintText="Extra text used when matching questions to this link—not shown as the main title. Leave blank if you don't need it."
                  />
                  <Textarea
                    id="mrl-desc"
                    aria-describedby="mrl-desc-hint"
                    value={form.description ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    rows={2}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={form.title.trim() === "" || generateDescriptionMutation.isPending}
                    onClick={() => generateDescriptionMutation.mutate()}
                  >
                    {generateDescriptionMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating…
                      </>
                    ) : (
                      "Generate description"
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Drafts from the title, resource type, and keywords. Review before saving.
                  </p>
                </div>
                <div className="space-y-2">
                  <LabelWithInfoTooltip
                    htmlFor="mrl-type"
                    label="Resource type (optional)"
                    hintId="mrl-type-hint"
                    hintText="Simple category tag (e.g. permit, form, fee schedule). Helps the assistant group similar resources."
                  />
                  <Input
                    id="mrl-type"
                    aria-describedby="mrl-type-hint"
                    value={form.resource_type ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, resource_type: e.target.value }))}
                    placeholder="permit"
                  />
                </div>
                <div className="space-y-2">
                  <LabelWithInfoTooltip
                    htmlFor="mrl-kw"
                    label="Keywords"
                    hintId="mrl-kw-hint"
                    hintText="Words or phrases residents might use. Separate with commas or new lines. At least one keyword is required—the assistant uses these to decide when to suggest this link."
                  />
                  <Textarea
                    id="mrl-kw"
                    aria-describedby="mrl-kw-hint"
                    value={keywordsText}
                    onChange={(e) => setKeywordsText(e.target.value)}
                    placeholder="permit, building permit, application"
                    rows={3}
                  />
                </div>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                  <div className="space-y-2 sm:flex-1">
                    <LabelWithInfoTooltip
                      htmlFor="mrl-priority"
                      label="Priority"
                      hintId="mrl-priority-hint"
                      hintText="When several links match, lower numbers are tried first (0 is highest priority)."
                    />
                    <Input
                      id="mrl-priority"
                      type="number"
                      min={0}
                      step={1}
                      aria-describedby="mrl-priority-hint"
                      value={form.priority}
                      onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="space-y-2 sm:flex-1 sm:pt-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="mrl-active"
                        checked={form.active}
                        onCheckedChange={(checked) => setForm((f) => ({ ...f, active: checked }))}
                        aria-describedby="mrl-active-hint"
                      />
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="mrl-active">Active</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex shrink-0 rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              aria-label="More about Active"
                            >
                              <Info className="h-3.5 w-3.5" aria-hidden />
                            </button>
                          </TooltipTrigger>
                          <TooltipPortal>
                            <TooltipContent
                              side="bottom"
                              sideOffset={6}
                              collisionPadding={16}
                              className="z-[100] max-w-xs text-left font-normal"
                            >
                              Inactive links are ignored—they won&apos;t be suggested until turned back on.
                            </TooltipContent>
                          </TooltipPortal>
                        </Tooltip>
                      </div>
                    </div>
                    <span id="mrl-active-hint" className="sr-only">
                      Inactive links are ignored—they won&apos;t be suggested until turned back on.
                    </span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setDialogOpen(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={submitForm}
                  disabled={
                    saving ||
                    !form.title.trim() ||
                    !form.url.trim() ||
                    !form.cta_label.trim() ||
                    parseKeywords(keywordsText).length === 0
                  }
                >
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {editingLink ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </TooltipProvider>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this link?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget ? `“${deleteTarget.title}” will be removed permanently.` : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={(e) => {
                  e.preventDefault();
                  if (deleteTarget) {
                    deleteMutation.mutate(deleteTarget.id);
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
